import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { RELEASE_ROLLOUT } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../../common/types';
import { useGetReleasePlanQuery } from '../../../../../redux/api/fymAPI';
import { Panel, PanelBody, PanelHead } from '../../../components/primitives/Panel';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { planPostState } from './planModel';
import { addDays } from './planMode';
import { planMessages } from './planMessages';
import { planDayDate } from './rolloutDayCopy';
import { RolloutTimeline } from './RolloutTimeline';

interface ClosedRolloutLineProps {
  accountId: number;
  catalogItemUuid: string;
  /** `YYYY-MM-DD`. */
  releaseDate: string;
  today: string;
}

const FIRST_OFFSET = Math.min(...RELEASE_ROLLOUT.map((day) => day.offsetDays));
const LAST_OFFSET = Math.max(...RELEASE_ROLLOUT.map((day) => day.offsetDays));

/**
 * The release rollout of a released song (design f2): one closed line ("29 May to 19 Jun · 10
 * posted · 1 failed"). "Show" opens the same timeline as f1, read only.
 */
const ClosedRolloutLineComponent: React.FC<ClosedRolloutLineProps> = ({
  accountId,
  catalogItemUuid,
  releaseDate,
  today,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((open) => !open), []);
  const plan = useGetReleasePlanQuery({ accountId, catalogItemUuid, kind: 'release', platforms: [] });
  const counts = useMemo(() => {
    const states = (plan.data?.days ?? []).flatMap((day) => day.posts.map((post) => planPostState(post.status)));
    return {
      posted: states.filter((state) => state === 'posted').length,
      failed: states.filter((state) => state === 'failed').length,
    };
  }, [plan.data]);
  const line = intl.formatMessage(planMessages.closedRollout, {
    from: planDayDate(intl, addDays(releaseDate, FIRST_OFFSET)),
    to: planDayDate(intl, addDays(releaseDate, LAST_OFFSET)),
    ...counts,
  });

  return (
    <Panel as="section" aria-label={intl.formatMessage(planMessages.rolloutTitle)}>
      <PanelHead
        title={intl.formatMessage(planMessages.rolloutTitle)}
        subtitle={line}
        right={
          <SMLButton onClick={toggle} ariaExpanded={isOpen}>
            {intl.formatMessage(isOpen ? planMessages.hide : planMessages.show)}
          </SMLButton>
        }
      />
      {isOpen && plan.data !== undefined && (
        <PanelBody>
          <RolloutTimeline days={plan.data.days} kind="release" today={today} readOnly />
        </PanelBody>
      )}
    </Panel>
  );
};

export const ClosedRolloutLine = TypedMemo(ClosedRolloutLineComponent);
